import dayjs from "dayjs";
import { z } from "zod";
import type { EncounterFilters } from "./useFilteredEncounters";

const CUSTOMER_PARAM = "customer";
const PATIENT_PARAM = "patient";
const DISCHARGE_FROM_PARAM = "dischargeFrom";
const DISCHARGE_TO_PARAM = "dischargeTo";

const DATE_PARAM_FORMAT = "YYYY-MM-DD";
const DATE_PARAM_PATTERN = /^(\d{4})-(\d{2})-(\d{2})$/;

const parseDateParam = (value: string | null): Date | null => {
  const match = value === null ? null : DATE_PARAM_PATTERN.exec(value);
  if (!match) return null;
  const [, year, month, day] = match;
  const date = new Date(Number(year), Number(month) - 1, Number(day));
  return dayjs(date).format(DATE_PARAM_FORMAT) === value ? date : null;
};

export const parsePatientId = (value: string | null): string | null => {
  const parsed = z.uuid().safeParse(value);
  return parsed.success ? parsed.data : null;
};

export const readEncounterFilterParams = (params: URLSearchParams): EncounterFilters => ({
  customerIds: params.getAll(CUSTOMER_PARAM).filter(Boolean),
  patientId: parsePatientId(params.get(PATIENT_PARAM)),
  dischargeFrom: parseDateParam(params.get(DISCHARGE_FROM_PARAM)),
  dischargeTo: parseDateParam(params.get(DISCHARGE_TO_PARAM)),
});

const setOrDelete = (params: URLSearchParams, key: string, value: string | null) => {
  if (value === null) {
    params.delete(key);
    return;
  }
  params.set(key, value);
};

const formatDateParam = (date: Date | null): string | null =>
  date === null ? null : dayjs(date).format(DATE_PARAM_FORMAT);

export const writeEncounterFilterParams = (params: URLSearchParams, filters: EncounterFilters): URLSearchParams => {
  const next = new URLSearchParams(params);
  next.delete(CUSTOMER_PARAM);
  for (const customerId of filters.customerIds) {
    next.append(CUSTOMER_PARAM, customerId);
  }

  setOrDelete(next, PATIENT_PARAM, filters.patientId);
  setOrDelete(next, DISCHARGE_FROM_PARAM, formatDateParam(filters.dischargeFrom));
  setOrDelete(next, DISCHARGE_TO_PARAM, formatDateParam(filters.dischargeTo));
  return next;
};
